import * as React from 'react'
import { cn } from '@/lib/utils'
import { fmtCZK } from './helpers'

// Micro data visualizations for listing cards — cards.jsx:42-116 + 182-196.
// Pools are passed explicitly (the prototype fell back to a global LISTINGS
// array); with no usable pool the components render nothing.

function medianOf(arr: number[]): number {
  const s = arr.slice().sort((a, b) => a - b)
  const m = s.length / 2
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[m - 1] + s[m]) / 2
}

// Neutral micro position bar: dot = this listing, tick = median of the pool.
export interface PositionBarProps {
  value: number
  pool?: number[]
  width?: number | string
  title?: string
}

export function PositionBar({ value, pool, width = '100%', title }: PositionBarProps) {
  if (!pool || pool.length < 2) return null
  const min = Math.min(...pool)
  const max = Math.max(...pool)
  if (max === min) return null
  const med = medianOf(pool)
  const pos = Math.min(95, Math.max(5, ((value - min) / (max - min)) * 100))
  const medPos = ((med - min) / (max - min)) * 100
  return (
    <span title={title} className="relative block h-1 shrink-0 rounded-[2px] bg-surface-high" style={{ width }}>
      <span
        className="absolute top-[-2.5px] h-[9px] w-[2px] rounded-[1px] bg-outline"
        style={{ left: `calc(${medPos}% - 1px)` }}
      />
      <span
        className="absolute top-[-1.5px] h-[7px] w-[7px] rounded-full bg-ink-muted shadow-[0_0_0_1.5px_rgb(var(--surface))]"
        style={{ left: `calc(${pos}% - 3.5px)` }}
      />
    </span>
  )
}

export interface AreaBarProps {
  listing: { area: number }
  width?: number | string
  pool?: number[]
}

export function AreaBar({ listing, width = '100%', pool }: AreaBarProps) {
  const areas = pool && pool.length ? pool : []
  const med = Math.round(medianOf(areas))
  return <PositionBar value={listing.area} pool={areas} width={width} title={`${listing.area} m² · page median ${med} m²`} />
}

// Rooms as little square "rooms".
// One square per room so the viz reads the room count directly. Capped at 6 to
// match the room filter's "6+" bucket (and to stay within the stat cell); the
// value text shows the exact number for the rare 7+ flat.
export function RoomSquares({ n, size = 10 }: { n: number; size?: number }) {
  const count = Math.min(Math.max(Math.round(n), 0), 6)
  return (
    <span className="flex items-center gap-[3px]">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="rounded-[3px]"
          style={{
            width: size,
            height: size,
            background: 'color-mix(in srgb, rgb(var(--ink-muted)) 50%, rgb(var(--surface-high)))',
          }}
        />
      ))}
    </span>
  )
}

// Area as a footprint vs the page median: dashed outline = median flat,
// filled rectangle = this one (side ∝ area ratio) — fits inside or spills out.
export interface AreaFootprintProps {
  listing: { area: number }
  pool?: number[]
  scale?: number
}

export function AreaFootprint({ listing, pool, scale = 1 }: AreaFootprintProps) {
  const areas = pool && pool.length ? pool : []
  if (!areas.length) return null
  const med = medianOf(areas)
  // Linear ratio (not √) so size differences read strongly.
  const s = Math.min(1.6, Math.max(0.3, listing.area / med))
  const W = Math.round(32 * scale)
  const H = Math.round(19 * scale) // the median footprint
  const boxW = Math.ceil(W * 1.6)
  const boxH = Math.ceil(H * 1.6)
  return (
    <span
      title={`${listing.area} m² · page median ${Math.round(med)} m² (dashed)`}
      className="relative inline-block shrink-0"
      style={{ width: boxW, height: boxH }}
    >
      <span
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[5px] border-[1.5px] border-dashed border-outline"
        style={{ width: W, height: H }}
      />
      <span
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[5px]"
        style={{
          width: Math.round(W * s),
          height: Math.round(H * s),
          background: 'color-mix(in srgb, rgb(var(--ink-muted)) 32%, transparent)',
          border: '1px solid color-mix(in srgb, rgb(var(--ink-muted)) 55%, transparent)',
        }}
      />
    </span>
  )
}

// Floor on a 0 → top-floor scale (ground is always the bottom of the line).
// `building` mode scales against the listing's own building instead of the
// page pool, with no median tick — for context-free surfaces like the detail.
export interface FloorGaugeProps {
  listing: { floor: number; totalFloors?: number }
  pool?: number[]
  height?: number | 'auto'
  building?: boolean
}

export function FloorGauge({ listing, pool, height = 26, building = false }: FloorGaugeProps) {
  const floors = pool && pool.length ? pool : []
  if (!building && floors.length < 2) return null
  const max = building ? Math.max(listing.totalFloors || 1, listing.floor) : Math.max(...floors, listing.floor)
  if (max <= 0) return null
  const med = building ? null : medianOf(floors)
  const pos = Math.min(96, Math.max(4, (listing.floor / max) * 100))
  const medPos = med == null ? null : Math.min(96, (med / max) * 100)
  const auto = height === 'auto'
  return (
    <span
      title={
        building
          ? `Floor ${listing.floor} of ${max}`
          : `Floor ${listing.floor} · page median ${Math.round(med as number)} · line runs 0–${max}`
      }
      className={cn('relative inline-block w-1 shrink-0 rounded-[2px] bg-surface-high', auto && 'self-stretch')}
      style={{ height: auto ? 'auto' : height, minHeight: auto ? 40 : undefined }}
    >
      {medPos != null ? (
        <span
          className="absolute left-[-2.5px] h-[2px] w-[9px] rounded-[1px] bg-outline"
          style={{ bottom: `calc(${medPos}% - 1px)` }}
        />
      ) : null}
      <span
        className="absolute left-[-1.5px] h-[7px] w-[7px] rounded-full bg-ink-muted shadow-[0_0_0_1.5px_rgb(var(--surface))]"
        style={{ bottom: `calc(${pos}% - 3.5px)` }}
      />
    </span>
  )
}

// Where this price sits within the current page's range, with a median tick.
export interface PriceStats {
  min: number
  max: number
  median: number
}

export function PricePositionBar({ listing, stats }: { listing: { price: number }; stats?: PriceStats }) {
  if (!stats || stats.max === stats.min) return null
  const pos = ((listing.price - stats.min) / (stats.max - stats.min)) * 100
  const medPos = ((stats.median - stats.min) / (stats.max - stats.min)) * 100
  const diff = (listing.price - stats.median) / stats.median
  const label =
    Math.abs(diff) < 0.005
      ? 'right at the page median'
      : `${Math.abs(Math.round(diff * 100))}% ${diff < 0 ? 'below' : 'above'} the page median`
  return (
    <div title={`${fmtCZK(listing.price)} — ${label}`} className="relative mt-[10px] h-[3px] rounded-[2px] bg-surface-high">
      <span
        className="absolute top-[-3px] h-[9px] w-[2px] rounded-[1px] bg-outline opacity-90"
        style={{ left: `calc(${medPos}% - 1px)` }}
      />
      <span
        className="absolute top-[-4px] h-[11px] w-[11px] rounded-full bg-ink-muted shadow-[0_0_0_2px_rgb(var(--surface-low))]"
        style={{ left: `calc(${Math.min(99, Math.max(1, pos))}% - 5.5px)` }}
      />
    </div>
  )
}
